'use client'

import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  ScriptRequestFormContext,
  type PrescriptionStage,
} from '@/features/prescriptions/request/ScriptRequestFormProvider'
import { useStore } from 'zustand'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Prescription } from '@/lib/questionnaires/Prescription'
import { extractFieldIds } from '@/shared/containers/QuestionnairePageContainer'
import { PrescriptionQuestionnaireFields } from '@/features/prescriptions/request/PrescriptionQuestionnaireFields'

type QuestionnaireStage = Extract<PrescriptionStage, { type: 'QUESTIONNAIRE' }>

type Props = {
  stage: QuestionnaireStage
  stageIndex: number
}

const ValidationSchema = Prescription.Validator

export default function ScriptQuestionnaireStageContainer({
  stage,
  stageIndex,
}: Props) {
  const store = useContext(ScriptRequestFormContext)
  if (!store)
    throw new Error(
      'PrescriptionQuestionnaireFormV2 must be used within a ScriptRequestFormProvider',
    )

  const onNext = useStore(store, (s) => s.onNext)
  const onPrev = useStore(store, (s) => s.onPrev)
  const questionnaireFormValues = useStore(
    store,
    (s) => s.questionnaireForm.values,
  )
  const updateQuestionnaireFormValues = useStore(
    store,
    (s) => s.updateQuestionnaireFormValues,
  )

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onChange',
    defaultValues: questionnaireFormValues,
    resolver: yupResolver(ValidationSchema, { abortEarly: false }),
  })

  const { watch, trigger, getValues, setValue } = methods
  const advancing = useRef(false)
  const [isAdvancing, setIsAdvancing] = useState(false)

  // Validate the form on mount, and persist values in the request store as
  // patients answer each question.
  useEffect(() => {
    trigger().then(() => {})

    const { unsubscribe } = watch((value) => {
      updateQuestionnaireFormValues(ValidationSchema.cast(value))
    })

    return () => unsubscribe()
  }, [trigger, updateQuestionnaireFormValues, watch])

  const currentStep = Prescription.createQuestions(
    stage.page,
    questionnaireFormValues,
  )
  if (
    currentStep === 'upload-supporting-document' ||
    currentStep === 'personal-information' ||
    currentStep === 'call-arrangement'
  )
    return null

  const fieldIds = extractFieldIds(currentStep) as Array<
    keyof typeof questionnaireFormValues
  >

  async function goForward() {
    if (advancing.current) return
    advancing.current = true
    setIsAdvancing(true)
    try {
      for (const name of fieldIds) {
        setValue(name, getValues(name), { shouldTouch: true })
      }
      if (await trigger(fieldIds, { shouldFocus: true })) onNext()
    } finally {
      advancing.current = false
      setIsAdvancing(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void goForward()
        }}
      >
        <PrescriptionQuestionnaireFields
          currentStep={currentStep}
          firstPage={stageIndex === 0}
          onBack={onPrev}
          busy={isAdvancing}
        />
      </form>
    </FormProvider>
  )
}
